// src/pages/trading-portal/OptionsContractManagement/ExpirationSettlementPage.js
// Expiration & Settlement page with 5-tab interface

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    TextField,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Autocomplete,
    Tabs,
    Tab,
    Tooltip,
    Switch,
    FormControlLabel,
    LinearProgress,
    Snackbar,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    NotificationsActive as AlertIcon,
    Search as SearchIcon,
    PlayArrow as RunIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    ExpirationAlertType, ExpirationAlertTypeLabels, ExpirationAlertTypeColors,
    AlertActionStatus, AlertActionStatusLabels, AlertActionStatusColors,
    AutoCloseRuleStatus, AutoCloseRuleStatusLabels, AutoCloseRuleStatusColors,
    SettlementType, SettlementTypeLabels, SettlementTypeColors,
    ExerciseStyle, ExerciseStyleLabels,
    OptionType, OptionTypeLabels, OptionTypeColors,
    PinRiskLevel, PinRiskLevelLabels, PinRiskLevelColors,
    ExerciseDecisionType, ExerciseDecisionTypeLabels, ExerciseDecisionTypeColors,
    StrategyType, StrategyTypeLabels,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const ExpirationSettlementPage = () => {
    // ==================== PERMISSIONS ====================
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Shared
    const [symbolOptions, setSymbolOptions] = useState([]);
    const [positionOptions, setPositionOptions] = useState([]);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // ==================== TAB 0: EXPIRATION ALERTS ====================
    const [alertsData, setAlertsData] = useState([]);
    const [alertsLoading, setAlertsLoading] = useState(false);
    const [alertsPage, setAlertsPage] = useState(0);
    const [alertsPerPage, setAlertsPerPage] = useState(10);
    const [alertsTotal, setAlertsTotal] = useState(0);
    const [alertsStatusFilter, setAlertsStatusFilter] = useState('');
    const [generatingAlerts, setGeneratingAlerts] = useState(false);
    const [alertStatusDialog, setAlertStatusDialog] = useState(false);
    const [alertStatusTarget, setAlertStatusTarget] = useState(null);
    const [alertStatusValue, setAlertStatusValue] = useState('');

    // ==================== TAB 1: AUTO-CLOSE RULES ====================
    const [rulesData, setRulesData] = useState([]);
    const [rulesLoading, setRulesLoading] = useState(false);
    const [rulesPage, setRulesPage] = useState(0);
    const [rulesPerPage, setRulesPerPage] = useState(10);
    const [rulesTotal, setRulesTotal] = useState(0);
    const [rulesDialog, setRulesDialog] = useState(false);
    const [rulesForm, setRulesForm] = useState({});
    const [rulesEditing, setRulesEditing] = useState(null);
    const [rulesDeleteDialog, setRulesDeleteDialog] = useState(false);
    const [rulesDeleteTarget, setRulesDeleteTarget] = useState(null);
    const [evaluatingRules, setEvaluatingRules] = useState(false);
    const [evaluateResults, setEvaluateResults] = useState([]);
    const [evaluateResultsDialog, setEvaluateResultsDialog] = useState(false);

    // ==================== TAB 2: SETTLEMENT RECORDS ====================
    const [settlementsData, setSettlementsData] = useState([]);
    const [settlementsLoading, setSettlementsLoading] = useState(false);
    const [settlementsPage, setSettlementsPage] = useState(0);
    const [settlementsPerPage, setSettlementsPerPage] = useState(10);
    const [settlementsTotal, setSettlementsTotal] = useState(0);
    const [settlementsSearch, setSettlementsSearch] = useState('');
    const [settlementsTypeFilter, setSettlementsTypeFilter] = useState('');
    const [settlementsDialog, setSettlementsDialog] = useState(false);
    const [settlementsForm, setSettlementsForm] = useState({});

    // ==================== TAB 3: PIN RISK ALERTS ====================
    const [pinRiskData, setPinRiskData] = useState([]);
    const [pinRiskLoading, setPinRiskLoading] = useState(false);
    const [pinRiskPage, setPinRiskPage] = useState(0);
    const [pinRiskPerPage, setPinRiskPerPage] = useState(10);
    const [pinRiskTotal, setPinRiskTotal] = useState(0);
    const [pinRiskLevelFilter, setPinRiskLevelFilter] = useState('');
    const [pinRiskDaysWindow, setPinRiskDaysWindow] = useState(7);
    const [scanningPinRisk, setScanningPinRisk] = useState(false);
    const [pinRiskScanResult, setPinRiskScanResult] = useState(null); // { message, severity }

    // ==================== TAB 4: EXERCISE DECISIONS ====================
    const [decisionsData, setDecisionsData] = useState([]);
    const [decisionsLoading, setDecisionsLoading] = useState(false);
    const [decisionsPage, setDecisionsPage] = useState(0);
    const [decisionsPerPage, setDecisionsPerPage] = useState(10);
    const [decisionsTotal, setDecisionsTotal] = useState(0);
    const [decisionsPositionFilter, setDecisionsPositionFilter] = useState('');
    const [analyzeDialog, setAnalyzeDialog] = useState(false);
    const [analyzePositionId, setAnalyzePositionId] = useState(null);
    const [analyzingExercise, setAnalyzingExercise] = useState(false);

    // ==================== PERMISSION CHECK ====================
    useEffect(() => {
        const checkPerms = async () => {
            const [view, add, edit, del] = await Promise.all([
                hasPermission('tr-ocm-expiration', 'view'),
                hasPermission('tr-ocm-expiration', 'add'),
                hasPermission('tr-ocm-expiration', 'edit'),
                hasPermission('tr-ocm-expiration', 'delete'),
            ]);
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPerms();
        fetchSymbols();
        fetchPositions();
    }, []);

    // ==================== FETCH SYMBOLS ====================
    const fetchSymbols = async () => {
        try {
            const res = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            setSymbolOptions(res.data.data || []);
        } catch (err) {
            console.error('Failed to fetch symbols:', err);
        }
    };

    // ==================== FETCH POSITIONS (for dropdowns) ====================
    const fetchPositions = async () => {
        try {
            const res = await axiosInstance.get('/api/options-contract-management/positions?per_page=100');
            setPositionOptions(res.data.data || []);
        } catch (err) {
            console.error('Failed to fetch positions:', err);
        }
    };

    // ==================== FORMAT HELPERS ====================
    const formatPrice = (price) => price != null ? `$${parseFloat(price).toFixed(2)}` : '-';
    const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
    const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '-';
    const formatPercent = (val) => val != null ? `${(parseFloat(val) * 100).toFixed(2)}%` : '-';

    // ==================== TAB 0: EXPIRATION ALERTS HANDLERS ====================

    const fetchAlerts = useCallback(async () => {
        if (!canView) return;
        setAlertsLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ page: alertsPage + 1, per_page: alertsPerPage });
            if (alertsStatusFilter) params.append('action_status', alertsStatusFilter);
            const res = await axiosInstance.get(`/api/options-contract-management/expiration-alerts?${params.toString()}`);
            if (res.data.success) {
                setAlertsData(res.data.data || []);
                setAlertsTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch expiration alerts');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch expiration alerts');
        } finally {
            setAlertsLoading(false);
        }
    }, [canView, alertsPage, alertsPerPage, alertsStatusFilter]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchAlerts();
    }, [fetchAlerts, canView, activeTab]);

    const handleGenerateAlerts = async () => {
        setGeneratingAlerts(true);
        setError('');
        try {
            const res = await axiosInstance.post('/api/options-contract-management/expiration-alerts/generate');
            if (res.data.success) {
                fetchAlerts();
            } else {
                setError(res.data.message || 'Failed to generate alerts');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to generate alerts');
        } finally {
            setGeneratingAlerts(false);
        }
    };

    const handleAlertStatusOpen = (row) => {
        setAlertStatusTarget(row);
        setAlertStatusValue('');
        setAlertStatusDialog(true);
    };

    const handleAlertStatusSave = async () => {
        if (!alertStatusTarget || !alertStatusValue) return;
        setSubmitting(true);
        try {
            const res = await axiosInstance.put(`/api/options-contract-management/expiration-alerts/${alertStatusTarget.id}/status`, {
                action_status: alertStatusValue,
            });
            if (res.data.success) {
                setAlertStatusDialog(false);
                setAlertStatusTarget(null);
                fetchAlerts();
            } else {
                setError(res.data.message || 'Failed to update alert status');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update alert status');
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== TAB 1: AUTO-CLOSE RULES HANDLERS ====================

    const fetchRules = useCallback(async () => {
        if (!canView) return;
        setRulesLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ page: rulesPage + 1, per_page: rulesPerPage });
            const res = await axiosInstance.get(`/api/options-contract-management/auto-close-rules?${params.toString()}`);
            if (res.data.success) {
                setRulesData(res.data.data || []);
                setRulesTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch auto-close rules');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch auto-close rules');
        } finally {
            setRulesLoading(false);
        }
    }, [canView, rulesPage, rulesPerPage]);

    useEffect(() => {
        if (canView && activeTab === 1) fetchRules();
    }, [fetchRules, canView, activeTab]);

    const handleRulesOpenDialog = (row = null) => {
        if (row) {
            setRulesEditing(row);
            const matchedSymbol = symbolOptions.find(s => s.id === row.symbol_id) || null;
            setRulesForm({
                rule_name: row.rule_name || '',
                position_id: row.position_id || '',
                symbol_id: matchedSymbol,
                status: row.status || '',
                close_if_value_below: row.close_if_value_below ?? '',
                close_if_itm_pct_below: row.close_if_itm_pct_below ?? '',
                close_dte_threshold: row.close_dte_threshold ?? '',
                close_if_profit_pct_above: row.close_if_profit_pct_above ?? '',
                close_if_loss_pct_above: row.close_if_loss_pct_above ?? '',
                close_time_on_expiry_day: row.close_time_on_expiry_day || '',
                strategy_type_filter: row.strategy_type_filter || '',
                is_global: row.is_global || false,
            });
        } else {
            setRulesEditing(null);
            setRulesForm({
                rule_name: '',
                position_id: '',
                symbol_id: null,
                status: AutoCloseRuleStatus.ACTIVE,
                close_if_value_below: '',
                close_if_itm_pct_below: '',
                close_dte_threshold: '',
                close_if_profit_pct_above: '',
                close_if_loss_pct_above: '',
                close_time_on_expiry_day: '',
                strategy_type_filter: '',
                is_global: false,
            });
        }
        setRulesDialog(true);
    };

    const handleRulesSave = async () => {
        setSubmitting(true);
        try {
            const payload = {
                rule_name: rulesForm.rule_name,
                position_id: rulesForm.position_id || null,
                symbol_id: rulesForm.symbol_id?.id || null,
                status: rulesForm.status,
                close_if_value_below: rulesForm.close_if_value_below !== '' ? parseFloat(rulesForm.close_if_value_below) : null,
                close_if_itm_pct_below: rulesForm.close_if_itm_pct_below !== '' ? parseFloat(rulesForm.close_if_itm_pct_below) : null,
                close_dte_threshold: rulesForm.close_dte_threshold !== '' ? parseInt(rulesForm.close_dte_threshold) : null,
                close_if_profit_pct_above: rulesForm.close_if_profit_pct_above !== '' ? parseFloat(rulesForm.close_if_profit_pct_above) : null,
                close_if_loss_pct_above: rulesForm.close_if_loss_pct_above !== '' ? parseFloat(rulesForm.close_if_loss_pct_above) : null,
                close_time_on_expiry_day: rulesForm.close_time_on_expiry_day || null,
                strategy_type_filter: rulesForm.strategy_type_filter || null,
                is_global: rulesForm.is_global,
            };
            let res;
            if (rulesEditing) {
                res = await axiosInstance.put(`/api/options-contract-management/auto-close-rules/${rulesEditing.id}`, payload);
            } else {
                res = await axiosInstance.post('/api/options-contract-management/auto-close-rules', payload);
            }
            if (res.data.success) {
                setRulesDialog(false);
                fetchRules();
            } else {
                setError(res.data.message || 'Failed to save auto-close rule');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save auto-close rule');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRulesDelete = async () => {
        if (!rulesDeleteTarget) return;
        try {
            const res = await axiosInstance.delete(`/api/options-contract-management/auto-close-rules/${rulesDeleteTarget.id}`);
            if (res.data.success) {
                setRulesDeleteDialog(false);
                setRulesDeleteTarget(null);
                fetchRules();
            } else {
                setError(res.data.message || 'Failed to delete auto-close rule');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete auto-close rule');
        }
    };

    const handleEvaluateRules = async () => {
        setEvaluatingRules(true);
        setError('');
        try {
            const res = await axiosInstance.post('/api/options-contract-management/auto-close-rules/evaluate');
            if (res.data.success) {
                setEvaluateResults(res.data.data || []);
                setEvaluateResultsDialog(true);
            } else {
                setError(res.data.message || 'Failed to evaluate rules');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to evaluate rules');
        } finally {
            setEvaluatingRules(false);
        }
    };

    // ==================== TAB 2: SETTLEMENT RECORDS HANDLERS ====================

    const fetchSettlements = useCallback(async () => {
        if (!canView) return;
        setSettlementsLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ page: settlementsPage + 1, per_page: settlementsPerPage });
            if (settlementsSearch) params.append('search', settlementsSearch);
            if (settlementsTypeFilter) params.append('settlement_type', settlementsTypeFilter);
            const res = await axiosInstance.get(`/api/options-contract-management/settlement-records?${params.toString()}`);
            if (res.data.success) {
                setSettlementsData(res.data.data || []);
                setSettlementsTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch settlement records');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch settlement records');
        } finally {
            setSettlementsLoading(false);
        }
    }, [canView, settlementsPage, settlementsPerPage, settlementsSearch, settlementsTypeFilter]);

    useEffect(() => {
        if (canView && activeTab === 2) fetchSettlements();
    }, [fetchSettlements, canView, activeTab]);

    const handleSettlementsOpenDialog = () => {
        setSettlementsForm({
            position_id: '',
            symbol_id: null,
            settlement_type: '',
            expiration_date: '',
            settlement_date: '',
            strike_price: '',
            option_type: '',
            underlying_settlement_price: '',
            settlement_value: '',
            shares_delivered: '',
            cash_amount: '',
            exercise_style: '',
            was_auto_exercised: false,
            was_assigned: false,
            settlement_notes: '',
        });
        setSettlementsDialog(true);
    };

    const handleSettlementsSave = async () => {
        setSubmitting(true);
        try {
            const payload = {
                position_id: settlementsForm.position_id || null,
                symbol_id: settlementsForm.symbol_id?.id || null,
                settlement_type: settlementsForm.settlement_type,
                expiration_date: settlementsForm.expiration_date,
                settlement_date: settlementsForm.settlement_date,
                strike_price: settlementsForm.strike_price !== '' ? parseFloat(settlementsForm.strike_price) : null,
                option_type: settlementsForm.option_type,
                underlying_settlement_price: settlementsForm.underlying_settlement_price !== '' ? parseFloat(settlementsForm.underlying_settlement_price) : null,
                settlement_value: settlementsForm.settlement_value !== '' ? parseFloat(settlementsForm.settlement_value) : null,
                shares_delivered: settlementsForm.shares_delivered !== '' ? parseInt(settlementsForm.shares_delivered) : null,
                cash_amount: settlementsForm.cash_amount !== '' ? parseFloat(settlementsForm.cash_amount) : null,
                exercise_style: settlementsForm.exercise_style,
                was_auto_exercised: settlementsForm.was_auto_exercised,
                was_assigned: settlementsForm.was_assigned,
                settlement_notes: settlementsForm.settlement_notes,
            };
            const res = await axiosInstance.post('/api/options-contract-management/settlement-records', payload);
            if (res.data.success) {
                setSettlementsDialog(false);
                fetchSettlements();
            } else {
                setError(res.data.message || 'Failed to save settlement record');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save settlement record');
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== TAB 3: PIN RISK ALERTS HANDLERS ====================

    const fetchPinRisk = useCallback(async () => {
        if (!canView) return;
        setPinRiskLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ page: pinRiskPage + 1, per_page: pinRiskPerPage });
            if (pinRiskLevelFilter) params.append('risk_level', pinRiskLevelFilter);
            const res = await axiosInstance.get(`/api/options-contract-management/pin-risk-alerts?${params.toString()}`);
            if (res.data.success) {
                setPinRiskData(res.data.data || []);
                setPinRiskTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch pin risk alerts');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch pin risk alerts');
        } finally {
            setPinRiskLoading(false);
        }
    }, [canView, pinRiskPage, pinRiskPerPage, pinRiskLevelFilter]);

    useEffect(() => {
        if (canView && activeTab === 3) fetchPinRisk();
    }, [fetchPinRisk, canView, activeTab]);

    const handleScanPinRisk = async () => {
        setScanningPinRisk(true);
        setError('');
        setPinRiskScanResult(null);
        try {
            const res = await axiosInstance.post('/api/options-contract-management/pin-risk-alerts/scan', { days: pinRiskDaysWindow });
            if (res.data.success) {
                const count = res.data.data?.length || 0;
                const msg = res.data.message || (count > 0
                    ? `Scan complete: Found ${count} pin risk alert${count !== 1 ? 's' : ''}`
                    : `Scan complete: No positions with pin risk detected within ${pinRiskDaysWindow} days of expiration`);
                setPinRiskScanResult({ message: msg, severity: count > 0 ? 'warning' : 'info' });
                fetchPinRisk();
            } else {
                setError(res.data.message || 'Failed to scan pin risk');
                setPinRiskScanResult({ message: res.data.message || 'Failed to scan pin risk', severity: 'error' });
            }
        } catch (err) {
            const errMsg = err.response?.data?.message || 'Failed to scan pin risk';
            setError(errMsg);
            setPinRiskScanResult({ message: errMsg, severity: 'error' });
        } finally {
            setScanningPinRisk(false);
        }
    };

    // ==================== TAB 4: EXERCISE DECISIONS HANDLERS ====================

    const fetchDecisions = useCallback(async () => {
        if (!canView) return;
        setDecisionsLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ page: decisionsPage + 1, per_page: decisionsPerPage });
            if (decisionsPositionFilter) params.append('position_id', decisionsPositionFilter);
            const res = await axiosInstance.get(`/api/options-contract-management/exercise-decisions?${params.toString()}`);
            if (res.data.success) {
                setDecisionsData(res.data.data || []);
                setDecisionsTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch exercise decisions');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch exercise decisions');
        } finally {
            setDecisionsLoading(false);
        }
    }, [canView, decisionsPage, decisionsPerPage, decisionsPositionFilter]);

    useEffect(() => {
        if (canView && activeTab === 4) fetchDecisions();
    }, [fetchDecisions, canView, activeTab]);

    const handleAnalyzeOpen = () => {
        setAnalyzePositionId(null);
        setAnalyzeDialog(true);
    };

    const handleAnalyzeExercise = async () => {
        if (!analyzePositionId) return;
        setAnalyzingExercise(true);
        setError('');
        try {
            const res = await axiosInstance.post(`/api/options-contract-management/exercise-decisions/${analyzePositionId}/analyze`);
            if (res.data.success) {
                setAnalyzeDialog(false);
                fetchDecisions();
            } else {
                setError(res.data.message || 'Failed to analyze exercise decision');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to analyze exercise decision');
        } finally {
            setAnalyzingExercise(false);
        }
    };

    // ==================== PERMISSION GATE ====================

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ fontFamily }}>
                    You do not have permission to view Expiration & Settlement.
                </Alert>
            </Box>
        );
    }

    // ==================== TAB 0 RENDER: EXPIRATION ALERTS ====================

    const renderExpirationAlerts = () => (
        <>
            {/* Filters & Actions Bar */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Action Status</InputLabel>
                            <Select
                                value={alertsStatusFilter}
                                label="Action Status"
                                onChange={(e) => { setAlertsStatusFilter(e.target.value); setAlertsPage(0); }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {Object.values(AlertActionStatus).map(s => (
                                    <MenuItem key={s} value={s}>{AlertActionStatusLabels[s]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchAlerts}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto" sx={{ ml: 'auto' }}>
                        <Button
                            variant="contained"
                            startIcon={generatingAlerts ? <CircularProgress size={18} color="inherit" /> : <AlertIcon />}
                            onClick={handleGenerateAlerts}
                            disabled={generatingAlerts}
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                        >
                            Generate Alerts
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Expiration Alerts Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                            <TableCell>Alert UUID</TableCell>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Expiration Date</TableCell>
                            <TableCell>Alert Type</TableCell>
                            <TableCell align="right">Days Remaining</TableCell>
                            <TableCell>Action Status</TableCell>
                            <TableCell>Recommended Action</TableCell>
                            <TableCell align="right">Position Value</TableCell>
                            <TableCell>Alert Message</TableCell>
                            <TableCell>Triggered At</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {alertsLoading ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                            </TableRow>
                        ) : alertsData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                    <AlertIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                    <Typography color="text.secondary" sx={{ fontFamily }}>No expiration alerts found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            alertsData.map(row => (
                                <TableRow key={row.id} hover sx={{ '& td': { fontFamily } }}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily, fontSize: '0.75rem', color: '#6b7280' }}>
                                            {row.alert_uuid ? row.alert_uuid.substring(0, 8) + '...' : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography fontWeight={600}>{row.symbol || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>{formatDate(row.expiration_date)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={ExpirationAlertTypeLabels[row.alert_type] || row.alert_type || '-'}
                                            color={ExpirationAlertTypeColors[row.alert_type] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{row.days_remaining ?? '-'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={AlertActionStatusLabels[row.action_status] || row.action_status || '-'}
                                            color={AlertActionStatusColors[row.action_status] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.recommended_action || '-'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{formatPrice(row.position_value)}</TableCell>
                                    <TableCell>
                                        <Tooltip title={row.alert_message || ''}>
                                            <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {row.alert_message || '-'}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>{formatDateTime(row.triggered_at)}</TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                            {canEdit && (
                                                <Tooltip title="Update Status">
                                                    <IconButton size="small" onClick={() => handleAlertStatusOpen(row)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={alertsTotal}
                page={alertsPage}
                onPageChange={(_, p) => setAlertsPage(p)}
                rowsPerPage={alertsPerPage}
                onRowsPerPageChange={(e) => { setAlertsPerPage(parseInt(e.target.value, 10)); setAlertsPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
            />
        </>
    );

    // ==================== TAB 1 RENDER: AUTO-CLOSE RULES ====================

    const renderAutoCloseRules = () => (
        <>
            {/* Actions Bar */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs="auto">
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchRules}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto">
                        <Button
                            variant="outlined"
                            startIcon={evaluatingRules ? <CircularProgress size={18} /> : <RunIcon />}
                            onClick={handleEvaluateRules}
                            disabled={evaluatingRules}
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderColor: ACCENT, color: ACCENT, '&:hover': { borderColor: '#d97706', backgroundColor: `${ACCENT}10` } }}
                        >
                            Evaluate Rules
                        </Button>
                    </Grid>
                    <Grid item xs="auto" sx={{ ml: 'auto' }}>
                        {canAdd && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleRulesOpenDialog()}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Add Rule
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* Auto-Close Rules Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                            <TableCell>Rule Name</TableCell>
                            <TableCell>Position ID</TableCell>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">DTE Threshold</TableCell>
                            <TableCell align="right">Profit % Above</TableCell>
                            <TableCell align="right">Loss % Above</TableCell>
                            <TableCell align="right">Value Below</TableCell>
                            <TableCell>Strategy Filter</TableCell>
                            <TableCell>Global</TableCell>
                            <TableCell align="right">Triggers</TableCell>
                            <TableCell>Last Triggered</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rulesLoading ? (
                            <TableRow>
                                <TableCell colSpan={13} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                            </TableRow>
                        ) : rulesData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                                    <RunIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                    <Typography color="text.secondary" sx={{ fontFamily }}>No auto-close rules found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rulesData.map(row => (
                                <TableRow key={row.id} hover sx={{ '& td': { fontFamily } }}>
                                    <TableCell>
                                        <Typography fontWeight={600}>{row.rule_name || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>{row.position_id ? `#${row.position_id}` : '-'}</TableCell>
                                    <TableCell>{row.symbol || '-'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={AutoCloseRuleStatusLabels[row.status] || row.status || '-'}
                                            color={AutoCloseRuleStatusColors[row.status] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{row.close_dte_threshold ?? '-'}</TableCell>
                                    <TableCell align="right">{row.close_if_profit_pct_above != null ? `${row.close_if_profit_pct_above}%` : '-'}</TableCell>
                                    <TableCell align="right">{row.close_if_loss_pct_above != null ? `${row.close_if_loss_pct_above}%` : '-'}</TableCell>
                                    <TableCell align="right">{formatPrice(row.close_if_value_below)}</TableCell>
                                    <TableCell>
                                        {row.strategy_type_filter ? (StrategyTypeLabels[row.strategy_type_filter] || row.strategy_type_filter) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.is_global ? 'Yes' : 'No'}
                                            color={row.is_global ? 'primary' : 'default'}
                                            size="small"
                                            variant={row.is_global ? 'filled' : 'outlined'}
                                        />
                                    </TableCell>
                                    <TableCell align="right">{row.triggers_count ?? '-'}</TableCell>
                                    <TableCell>{formatDateTime(row.last_triggered_at)}</TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                            {canEdit && (
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => handleRulesOpenDialog(row)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canDelete && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => { setRulesDeleteTarget(row); setRulesDeleteDialog(true); }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={rulesTotal}
                page={rulesPage}
                onPageChange={(_, p) => setRulesPage(p)}
                rowsPerPage={rulesPerPage}
                onRowsPerPageChange={(e) => { setRulesPerPage(parseInt(e.target.value, 10)); setRulesPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
            />
        </>
    );

    // ==================== TAB 2 RENDER: SETTLEMENT RECORDS ====================

    const renderSettlementRecords = () => (
        <>
            {/* Filters & Actions Bar */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search Symbol"
                            value={settlementsSearch}
                            onChange={(e) => { setSettlementsSearch(e.target.value); setSettlementsPage(0); }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Settlement Type</InputLabel>
                            <Select
                                value={settlementsTypeFilter}
                                label="Settlement Type"
                                onChange={(e) => { setSettlementsTypeFilter(e.target.value); setSettlementsPage(0); }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {Object.values(SettlementType).map(s => (
                                    <MenuItem key={s} value={s}>{SettlementTypeLabels[s]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchSettlements}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto" sx={{ ml: 'auto' }}>
                        {canAdd && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleSettlementsOpenDialog}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Add Settlement
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* Settlement Records Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                            <TableCell>Settlement UUID</TableCell>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Settlement Type</TableCell>
                            <TableCell>Expiration Date</TableCell>
                            <TableCell>Settlement Date</TableCell>
                            <TableCell align="right">Strike Price</TableCell>
                            <TableCell>Option Type</TableCell>
                            <TableCell align="right">Underlying Price</TableCell>
                            <TableCell align="right">Settlement Value</TableCell>
                            <TableCell align="right">Shares Delivered</TableCell>
                            <TableCell align="right">Cash Amount</TableCell>
                            <TableCell>Exercise Style</TableCell>
                            <TableCell>Auto Exercised</TableCell>
                            <TableCell>Assigned</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {settlementsLoading ? (
                            <TableRow>
                                <TableCell colSpan={14} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                            </TableRow>
                        ) : settlementsData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                                    <SearchIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                    <Typography color="text.secondary" sx={{ fontFamily }}>No settlement records found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            settlementsData.map(row => (
                                <TableRow key={row.id} hover sx={{ '& td': { fontFamily } }}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily, fontSize: '0.75rem', color: '#6b7280' }}>
                                            {row.settlement_uuid ? row.settlement_uuid.substring(0, 8) + '...' : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography fontWeight={600}>{row.symbol || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={SettlementTypeLabels[row.settlement_type] || row.settlement_type || '-'}
                                            color={SettlementTypeColors[row.settlement_type] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{formatDate(row.expiration_date)}</TableCell>
                                    <TableCell>{formatDate(row.settlement_date)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.strike_price)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={OptionTypeLabels[row.option_type] || row.option_type || '-'}
                                            color={OptionTypeColors[row.option_type] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{formatPrice(row.underlying_settlement_price)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.settlement_value)}</TableCell>
                                    <TableCell align="right">{row.shares_delivered ?? '-'}</TableCell>
                                    <TableCell align="right">{formatPrice(row.cash_amount)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={ExerciseStyleLabels[row.exercise_style] || row.exercise_style || '-'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.was_auto_exercised ? 'Yes' : 'No'}
                                            color={row.was_auto_exercised ? 'success' : 'default'}
                                            size="small"
                                            variant={row.was_auto_exercised ? 'filled' : 'outlined'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.was_assigned ? 'Yes' : 'No'}
                                            color={row.was_assigned ? 'warning' : 'default'}
                                            size="small"
                                            variant={row.was_assigned ? 'filled' : 'outlined'}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={settlementsTotal}
                page={settlementsPage}
                onPageChange={(_, p) => setSettlementsPage(p)}
                rowsPerPage={settlementsPerPage}
                onRowsPerPageChange={(e) => { setSettlementsPerPage(parseInt(e.target.value, 10)); setSettlementsPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
            />
        </>
    );

    // ==================== TAB 3 RENDER: PIN RISK ALERTS ====================

    const renderPinRiskAlerts = () => (
        <>
            {/* Filters & Actions Bar */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Risk Level</InputLabel>
                            <Select
                                value={pinRiskLevelFilter}
                                label="Risk Level"
                                onChange={(e) => { setPinRiskLevelFilter(e.target.value); setPinRiskPage(0); }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {Object.values(PinRiskLevel).map(s => (
                                    <MenuItem key={s} value={s}>{PinRiskLevelLabels[s]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchPinRisk}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto" sx={{ ml: 'auto' }}>
                        <TextField
                            size="small"
                            type="number"
                            label="Days Window"
                            value={pinRiskDaysWindow}
                            onChange={(e) => setPinRiskDaysWindow(Math.max(1, parseInt(e.target.value) || 1))}
                            inputProps={{ min: 1, max: 90 }}
                            sx={{ width: 110, mr: 1, fontFamily }}
                        />
                        <Button
                            variant="contained"
                            startIcon={scanningPinRisk ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                            onClick={handleScanPinRisk}
                            disabled={scanningPinRisk}
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                        >
                            Scan Pin Risk
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Scan Result Inline Alert */}
            {pinRiskScanResult && (
                <Alert
                    severity={pinRiskScanResult.severity}
                    onClose={() => setPinRiskScanResult(null)}
                    sx={{ mb: 2, fontFamily }}
                >
                    {pinRiskScanResult.message}
                </Alert>
            )}

            {/* Pin Risk Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                            <TableCell>Alert UUID</TableCell>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Expiration Date</TableCell>
                            <TableCell align="right">Short Strike</TableCell>
                            <TableCell align="right">Underlying Price</TableCell>
                            <TableCell align="right">Distance</TableCell>
                            <TableCell align="right">Distance %</TableCell>
                            <TableCell align="right">Gamma Exposure</TableCell>
                            <TableCell>Risk Level</TableCell>
                            <TableCell>Pin Risk Score</TableCell>
                            <TableCell>Recommended Action</TableCell>
                            <TableCell>Action Status</TableCell>
                            <TableCell>Detected At</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pinRiskLoading ? (
                            <TableRow>
                                <TableCell colSpan={13} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                            </TableRow>
                        ) : pinRiskData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                                    <AlertIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                    <Typography color="text.secondary" sx={{ fontFamily }}>No pin risk alerts found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            pinRiskData.map(row => (
                                <TableRow key={row.id} hover sx={{ '& td': { fontFamily } }}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily, fontSize: '0.75rem', color: '#6b7280' }}>
                                            {row.alert_uuid ? row.alert_uuid.substring(0, 8) + '...' : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography fontWeight={600}>{row.symbol || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>{formatDate(row.expiration_date)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.short_strike)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.underlying_price)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.distance_to_strike)}</TableCell>
                                    <TableCell align="right">{row.distance_to_strike_pct != null ? `${parseFloat(row.distance_to_strike_pct).toFixed(2)}%` : '-'}</TableCell>
                                    <TableCell align="right">{row.gamma_exposure != null ? parseFloat(row.gamma_exposure).toFixed(4) : '-'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={PinRiskLevelLabels[row.risk_level] || row.risk_level || '-'}
                                            color={PinRiskLevelColors[row.risk_level] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={row.pin_risk_score || 0}
                                                sx={{
                                                    width: 80, height: 8, borderRadius: 4,
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: row.pin_risk_score > 70 ? '#ef4444' : row.pin_risk_score > 40 ? '#f59e0b' : '#22c55e'
                                                    }
                                                }}
                                            />
                                            <Typography variant="body2">{(row.pin_risk_score || 0).toFixed(0)}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.recommended_action || '-'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={AlertActionStatusLabels[row.action_status] || row.action_status || '-'}
                                            color={AlertActionStatusColors[row.action_status] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{formatDateTime(row.detected_at)}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={pinRiskTotal}
                page={pinRiskPage}
                onPageChange={(_, p) => setPinRiskPage(p)}
                rowsPerPage={pinRiskPerPage}
                onRowsPerPageChange={(e) => { setPinRiskPerPage(parseInt(e.target.value, 10)); setPinRiskPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
            />
        </>
    );

    // ==================== TAB 4 RENDER: EXERCISE DECISIONS ====================

    const renderExerciseDecisions = () => (
        <>
            {/* Filters & Actions Bar */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6} sm={4} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Position</InputLabel>
                            <Select
                                value={decisionsPositionFilter}
                                label="Position"
                                onChange={(e) => { setDecisionsPositionFilter(e.target.value); setDecisionsPage(0); }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {positionOptions.map(p => (
                                    <MenuItem key={p.id} value={p.id}>
                                        {p.symbol} - {p.position_name || `Position #${p.id}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchDecisions}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto" sx={{ ml: 'auto' }}>
                        <Button
                            variant="contained"
                            startIcon={<RunIcon />}
                            onClick={handleAnalyzeOpen}
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                        >
                            Analyze Exercise
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Exercise Decisions Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                            <TableCell>Decision UUID</TableCell>
                            <TableCell>Symbol</TableCell>
                            <TableCell align="right">Strike Price</TableCell>
                            <TableCell>Expiration Date</TableCell>
                            <TableCell>Option Type</TableCell>
                            <TableCell>Exercise Style</TableCell>
                            <TableCell>Decision</TableCell>
                            <TableCell align="right">Intrinsic Value</TableCell>
                            <TableCell align="right">Time Value</TableCell>
                            <TableCell align="right">Underlying Price</TableCell>
                            <TableCell align="right">Margin Impact</TableCell>
                            <TableCell align="right">Opportunity Cost</TableCell>
                            <TableCell>Rationale</TableCell>
                            <TableCell>Optimal</TableCell>
                            <TableCell>Decided At</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {decisionsLoading ? (
                            <TableRow>
                                <TableCell colSpan={15} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                            </TableRow>
                        ) : decisionsData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={15} align="center" sx={{ py: 4 }}>
                                    <RunIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                    <Typography color="text.secondary" sx={{ fontFamily }}>No exercise decisions found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            decisionsData.map(row => (
                                <TableRow key={row.id} hover sx={{ '& td': { fontFamily } }}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily, fontSize: '0.75rem', color: '#6b7280' }}>
                                            {row.decision_uuid ? row.decision_uuid.substring(0, 8) + '...' : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography fontWeight={600}>{row.symbol || '-'}</Typography>
                                    </TableCell>
                                    <TableCell align="right">{formatPrice(row.strike_price)}</TableCell>
                                    <TableCell>{formatDate(row.expiration_date)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={OptionTypeLabels[row.option_type] || row.option_type || '-'}
                                            color={OptionTypeColors[row.option_type] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={ExerciseStyleLabels[row.exercise_style] || row.exercise_style || '-'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={ExerciseDecisionTypeLabels[row.decision] || row.decision || '-'}
                                            color={ExerciseDecisionTypeColors[row.decision] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{formatPrice(row.intrinsic_value)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.remaining_time_value)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.underlying_price)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.margin_impact)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.opportunity_cost)}</TableCell>
                                    <TableCell>
                                        <Tooltip title={row.decision_rationale || ''}>
                                            <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {row.decision_rationale || '-'}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.is_optimal ? 'Yes' : 'No'}
                                            color={row.is_optimal ? 'success' : 'default'}
                                            size="small"
                                            variant={row.is_optimal ? 'filled' : 'outlined'}
                                        />
                                    </TableCell>
                                    <TableCell>{formatDateTime(row.decided_at)}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={decisionsTotal}
                page={decisionsPage}
                onPageChange={(_, p) => setDecisionsPage(p)}
                rowsPerPage={decisionsPerPage}
                onRowsPerPageChange={(e) => { setDecisionsPerPage(parseInt(e.target.value, 10)); setDecisionsPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
            />
        </>
    );

    // ==================== MAIN RENDER ====================

    return (
        <Box sx={{ p: 3 }}>
            {/* Gradient Header */}
            <Box sx={{ background: 'linear-gradient(135deg, #1e1e2e 0%, #2d1b4e 100%)', borderRadius: 3, p: 4, mb: 3, position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: -40, right: -40, width: 150, height: 150, borderRadius: '50%', background: `${ACCENT}15` }} />
                <Box sx={{ position: 'absolute', bottom: -20, left: '30%', width: 100, height: 100, borderRadius: '50%', background: `${ACCENT}10` }} />
                <Typography variant="h4" fontWeight={700} sx={{ color: '#fff', fontFamily }}>Expiration & Settlement</Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', fontFamily, mt: 1 }}>
                    Expiration alerts, auto-close rules, settlement tracking, pin risk, and exercise decisions
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3, fontFamily }}>
                    {error}
                </Alert>
            )}
            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 600 },
                        '& .Mui-selected': { color: `${ACCENT} !important` },
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                    }}
                >
                    <Tab label="Expiration Alerts" />
                    <Tab label="Auto-Close Rules" />
                    <Tab label="Settlement Records" />
                    <Tab label="Pin Risk" />
                    <Tab label="Exercise Decisions" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderExpirationAlerts()}
            {activeTab === 1 && renderAutoCloseRules()}
            {activeTab === 2 && renderSettlementRecords()}
            {activeTab === 3 && renderPinRiskAlerts()}
            {activeTab === 4 && renderExerciseDecisions()}

            {/* ==================== DIALOGS ==================== */}

            {/* Update Alert Status Dialog */}
            <Dialog open={alertStatusDialog} onClose={() => setAlertStatusDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Update Alert Status</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>New Status</InputLabel>
                                <Select
                                    value={alertStatusValue}
                                    label="New Status"
                                    onChange={(e) => setAlertStatusValue(e.target.value)}
                                >
                                    <MenuItem value={AlertActionStatus.ACKNOWLEDGED}>{AlertActionStatusLabels.ACKNOWLEDGED}</MenuItem>
                                    <MenuItem value={AlertActionStatus.ACTED_ON}>{AlertActionStatusLabels.ACTED_ON}</MenuItem>
                                    <MenuItem value={AlertActionStatus.DISMISSED}>{AlertActionStatusLabels.DISMISSED}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setAlertStatusDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleAlertStatusSave}
                        disabled={submitting || !alertStatusValue}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        {submitting ? <CircularProgress size={20} /> : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create/Edit Auto-Close Rule Dialog */}
            <Dialog open={rulesDialog} onClose={() => setRulesDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {rulesEditing ? 'Edit Auto-Close Rule' : 'Create Auto-Close Rule'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Rule Name"
                                value={rulesForm.rule_name || ''}
                                onChange={(e) => setRulesForm({ ...rulesForm, rule_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Position (Optional)</InputLabel>
                                <Select
                                    value={rulesForm.position_id || ''}
                                    label="Position (Optional)"
                                    onChange={(e) => setRulesForm({ ...rulesForm, position_id: e.target.value })}
                                >
                                    <MenuItem value="">-- None --</MenuItem>
                                    {positionOptions.map(p => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.symbol} - {p.position_name || `Position #${p.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={symbolOptions}
                                getOptionLabel={(opt) => opt.symbol || ''}
                                value={rulesForm.symbol_id || null}
                                onChange={(_, val) => setRulesForm({ ...rulesForm, symbol_id: val })}
                                renderInput={(params) => <TextField {...params} label="Symbol (Optional)" size="small" fullWidth />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={rulesForm.status || ''}
                                    label="Status"
                                    onChange={(e) => setRulesForm({ ...rulesForm, status: e.target.value })}
                                >
                                    {Object.values(AutoCloseRuleStatus).map(s => (
                                        <MenuItem key={s} value={s}>{AutoCloseRuleStatusLabels[s]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Close If Value Below ($)"
                                type="number"
                                value={rulesForm.close_if_value_below ?? ''}
                                onChange={(e) => setRulesForm({ ...rulesForm, close_if_value_below: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Close If ITM % Below"
                                type="number"
                                value={rulesForm.close_if_itm_pct_below ?? ''}
                                onChange={(e) => setRulesForm({ ...rulesForm, close_if_itm_pct_below: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="DTE Threshold (days)"
                                type="number"
                                value={rulesForm.close_dte_threshold ?? ''}
                                onChange={(e) => setRulesForm({ ...rulesForm, close_dte_threshold: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Close If Profit % Above"
                                type="number"
                                value={rulesForm.close_if_profit_pct_above ?? ''}
                                onChange={(e) => setRulesForm({ ...rulesForm, close_if_profit_pct_above: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Close If Loss % Above"
                                type="number"
                                value={rulesForm.close_if_loss_pct_above ?? ''}
                                onChange={(e) => setRulesForm({ ...rulesForm, close_if_loss_pct_above: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Close Time On Expiry Day"
                                type="time"
                                value={rulesForm.close_time_on_expiry_day || ''}
                                onChange={(e) => setRulesForm({ ...rulesForm, close_time_on_expiry_day: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Strategy Type Filter</InputLabel>
                                <Select
                                    value={rulesForm.strategy_type_filter || ''}
                                    label="Strategy Type Filter"
                                    onChange={(e) => setRulesForm({ ...rulesForm, strategy_type_filter: e.target.value })}
                                >
                                    <MenuItem value="">-- None --</MenuItem>
                                    {Object.values(StrategyType).map(s => (
                                        <MenuItem key={s} value={s}>{StrategyTypeLabels[s]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={rulesForm.is_global || false}
                                        onChange={(e) => setRulesForm({ ...rulesForm, is_global: e.target.checked })}
                                        color="primary"
                                    />
                                }
                                label="Is Global"
                                sx={{ fontFamily }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setRulesDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleRulesSave}
                        disabled={submitting || !rulesForm.rule_name}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        {submitting ? <CircularProgress size={20} /> : (rulesEditing ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Auto-Close Rule Dialog */}
            <Dialog open={rulesDeleteDialog} onClose={() => setRulesDeleteDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Auto-Close Rule</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily }}>
                        Are you sure you want to delete the rule <strong>{rulesDeleteTarget?.rule_name}</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setRulesDeleteDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleRulesDelete}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Auto-Close Evaluate Results Dialog */}
            <Dialog open={evaluateResultsDialog} onClose={() => setEvaluateResultsDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Auto-Close Evaluation Results</DialogTitle>
                <DialogContent>
                    {evaluateResults.length === 0 ? (
                        <Alert severity="info">No positions match any active auto-close rules</Alert>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                                    <TableCell>Rule</TableCell>
                                    <TableCell>Position</TableCell>
                                    <TableCell>Symbol</TableCell>
                                    <TableCell>Reasons</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {evaluateResults.map((r, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{r.rule_name}</TableCell>
                                        <TableCell>#{r.position_id}</TableCell>
                                        <TableCell>{r.symbol}</TableCell>
                                        <TableCell>{r.reasons?.join(', ')}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setEvaluateResultsDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Add Settlement Record Dialog */}
            <Dialog open={settlementsDialog} onClose={() => setSettlementsDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Add Settlement Record</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Position</InputLabel>
                                <Select
                                    value={settlementsForm.position_id || ''}
                                    label="Position"
                                    onChange={(e) => setSettlementsForm({ ...settlementsForm, position_id: e.target.value })}
                                >
                                    <MenuItem value="">-- Select --</MenuItem>
                                    {positionOptions.map(p => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.symbol} - {p.position_name || `Position #${p.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={symbolOptions}
                                getOptionLabel={(opt) => opt.symbol || ''}
                                value={settlementsForm.symbol_id || null}
                                onChange={(_, val) => setSettlementsForm({ ...settlementsForm, symbol_id: val })}
                                renderInput={(params) => <TextField {...params} label="Symbol" size="small" fullWidth />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Settlement Type</InputLabel>
                                <Select
                                    value={settlementsForm.settlement_type || ''}
                                    label="Settlement Type"
                                    onChange={(e) => setSettlementsForm({ ...settlementsForm, settlement_type: e.target.value })}
                                >
                                    {Object.values(SettlementType).map(s => (
                                        <MenuItem key={s} value={s}>{SettlementTypeLabels[s]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Expiration Date"
                                type="date"
                                value={settlementsForm.expiration_date || ''}
                                onChange={(e) => setSettlementsForm({ ...settlementsForm, expiration_date: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Settlement Date"
                                type="date"
                                value={settlementsForm.settlement_date || ''}
                                onChange={(e) => setSettlementsForm({ ...settlementsForm, settlement_date: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Strike Price"
                                type="number"
                                value={settlementsForm.strike_price ?? ''}
                                onChange={(e) => setSettlementsForm({ ...settlementsForm, strike_price: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Option Type</InputLabel>
                                <Select
                                    value={settlementsForm.option_type || ''}
                                    label="Option Type"
                                    onChange={(e) => setSettlementsForm({ ...settlementsForm, option_type: e.target.value })}
                                >
                                    {Object.values(OptionType).map(s => (
                                        <MenuItem key={s} value={s}>{OptionTypeLabels[s]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Underlying Settlement Price"
                                type="number"
                                value={settlementsForm.underlying_settlement_price ?? ''}
                                onChange={(e) => setSettlementsForm({ ...settlementsForm, underlying_settlement_price: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Settlement Value"
                                type="number"
                                value={settlementsForm.settlement_value ?? ''}
                                onChange={(e) => setSettlementsForm({ ...settlementsForm, settlement_value: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Shares Delivered"
                                type="number"
                                value={settlementsForm.shares_delivered ?? ''}
                                onChange={(e) => setSettlementsForm({ ...settlementsForm, shares_delivered: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Cash Amount"
                                type="number"
                                value={settlementsForm.cash_amount ?? ''}
                                onChange={(e) => setSettlementsForm({ ...settlementsForm, cash_amount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Exercise Style</InputLabel>
                                <Select
                                    value={settlementsForm.exercise_style || ''}
                                    label="Exercise Style"
                                    onChange={(e) => setSettlementsForm({ ...settlementsForm, exercise_style: e.target.value })}
                                >
                                    {Object.values(ExerciseStyle).map(s => (
                                        <MenuItem key={s} value={s}>{ExerciseStyleLabels[s]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settlementsForm.was_auto_exercised || false}
                                        onChange={(e) => setSettlementsForm({ ...settlementsForm, was_auto_exercised: e.target.checked })}
                                        color="primary"
                                    />
                                }
                                label="Was Auto Exercised"
                                sx={{ fontFamily }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settlementsForm.was_assigned || false}
                                        onChange={(e) => setSettlementsForm({ ...settlementsForm, was_assigned: e.target.checked })}
                                        color="primary"
                                    />
                                }
                                label="Was Assigned"
                                sx={{ fontFamily }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Settlement Notes"
                                multiline
                                rows={3}
                                value={settlementsForm.settlement_notes || ''}
                                onChange={(e) => setSettlementsForm({ ...settlementsForm, settlement_notes: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setSettlementsDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSettlementsSave}
                        disabled={submitting}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        {submitting ? <CircularProgress size={20} /> : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Analyze Exercise Dialog */}
            <Dialog open={analyzeDialog} onClose={() => setAnalyzeDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Analyze Exercise Decision</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Select Position</InputLabel>
                                <Select
                                    value={analyzePositionId || ''}
                                    label="Select Position"
                                    onChange={(e) => setAnalyzePositionId(e.target.value)}
                                >
                                    <MenuItem value="">-- Select --</MenuItem>
                                    {positionOptions.map(p => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.symbol} - {p.position_name || `Position #${p.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setAnalyzeDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleAnalyzeExercise}
                        disabled={analyzingExercise || !analyzePositionId}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        {analyzingExercise ? <CircularProgress size={20} /> : 'Analyze'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Pin Risk Scan Snackbar */}
            <Snackbar
                open={!!pinRiskScanResult}
                autoHideDuration={6000}
                onClose={() => setPinRiskScanResult(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {pinRiskScanResult ? (
                    <Alert
                        onClose={() => setPinRiskScanResult(null)}
                        severity={pinRiskScanResult.severity}
                        variant="filled"
                        sx={{ width: '100%', fontFamily }}
                    >
                        {pinRiskScanResult.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Box>
    );
};

export default ExpirationSettlementPage;
